import * as React from "react";
import styled, { css } from "styled-components";

import Style from "@client/utils/Style";

import downSvg from "./images/down.svg";
import upSvg from "./images/up.svg";

const Wrapper = styled.div`
  position: relative;
`;
const getInputWidth = (props: any) => {
  if (props.width) {
    return props.width;
  }

  if (props.large) {
    return `${Style.inputSizes.large.width}px`;
  }

  return `${Style.inputSizes.regular.width}px`;
};
const borderColor = (props: any, defaultColor = Style.palette.grayscale[3]) =>
  props.highlight ? Style.palette.alert : defaultColor;
const Input = styled.input`
  width: ${props => getInputWidth(props)};
  height: ${(props: any) =>
    props.height || `${Style.inputSizes.regular.height}px`};
  line-height: ${(props: any) => props.lineHeight || "normal"};
  border-radius: ${Style.borderRadius};
  background-color: #fff;
  border: ${(props: any) =>
    props.embedded ? "0" : `1px solid ${borderColor(props)}`};
  border-top-left-radius: ${(props: any) =>
    props.embedded ? "0" : Style.borderRadius};
  border-top-right-radius: ${Style.borderRadius};
  border-bottom-left-radius: ${(props: any) =>
    props.embedded ? "0" : Style.borderRadius};
  border-bottom-right-radius: ${Style.borderRadius};
  color: ${Style.palette.black};
  padding: 0 8px 0 ${(props: any) => (props.embedded ? "0" : "16px")};
  font-size: inherit;
  transition: all ${Style.animations.swift};
  box-sizing: border-box;
  &:hover {
    border-color: ${(props: any) =>
      borderColor(
        props,
        props.disablePrimary ? undefined : Style.palette.primaryHighlight,
      )};
  }
  &:focus {
    border-color: ${(props: any) =>
      borderColor(
        props,
        props.disablePrimary ? undefined : Style.palette.primaryHighlight,
      )};
    outline: none;
  }
  &:disabled {
    color: ${Style.palette.grayscale[3]};
    border-color: ${Style.palette.grayscale[0]};
    background-color: ${Style.palette.grayscale[0]};
  }
  &::placeholder {
    color: ${Style.palette.grayscale[3]};
  }
`;
const StepsButtons = styled.div<{ embedded?: boolean }>`
  display: flex;
  flex-direction: column;
  position: absolute;
  right: ${p => (p.embedded ? "0" : "1px")};
  top: ${p => (p.embedded ? "0" : "1px")};
`;
const StepButton = css`
  background: #f1ecef;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StepButtonUp = styled.div`
  width: 17px;
  height: 15px;
  border-bottom: 1px solid ${Style.palette.secondaryLight};
  border-top-right-radius: 4px;
  ${StepButton}
`;
const StepButtonDown = styled.div`
  width: 17px;
  height: 14px;
  border-bottom-right-radius: 4px;
  ${StepButton}
`;
const StepImage = styled.div<{ up?: boolean; down?: boolean }>`
  width: 8px;
  height: 4px;
  background: url("${props => (props.up ? upSvg : downSvg)}") center no-repeat;
  transform: scale(1);
  animation: ${Style.animations.swift};
`;

type Props = {
  _ref?: (ref: HTMLElement) => void;
  disabled?: boolean;
  highlight?: boolean;
  large?: boolean;
  onChange?: (value: number) => void;
  type?: string;
  value?: number | null;
  embedded?: boolean;
  height?: string;
  width?: string;
  name?: string;
  placeholder?: string;
  style?: React.CSSProperties;
};
const Stepper = (props: Props) => {
  const { _ref, value, onChange, type } = props;
  let downImageRef: HTMLElement | null = null;
  let upImageRef: HTMLElement | null = null;
  const scale = (imageRef: HTMLElement | null, direction: "up" | "down") => {
    if (!imageRef) {
      return;
    }
    imageRef.style.transform = `scale(${direction === "down" ? "0.8" : "1"})`;
  };
  return (
    <Wrapper>
      <Input
        {...props}
        type={type || "text"}
        ref={ref => {
          if (_ref && ref) {
            _ref(ref);
          }
        }}
        value={value === null ? undefined : value}
        onChange={e => {
          const value = Number(e.target.value.replace(/\D/g, "")) || 0;
          if (onChange) {
            onChange(value);
          }
        }}
      />
      <StepsButtons embedded={props.embedded}>
        <StepButtonUp
          onClick={() => {
            if (onChange) {
              onChange((value || 0) + 1);
            }
          }}
          onMouseDown={() => {
            scale(upImageRef, "down");
          }}
          onMouseUp={() => {
            scale(upImageRef, "up");
          }}
          onMouseLeave={() => {
            scale(upImageRef, "up");
          }}
        >
          <StepImage
            up
            ref={ref => {
              upImageRef = ref;
            }}
          />
        </StepButtonUp>
        <StepButtonDown
          onClick={() => {
            if (onChange) {
              onChange(value && value > 0 ? value - 1 : 0);
            }
          }}
          onMouseDown={() => {
            scale(downImageRef, "down");
          }}
          onMouseUp={() => {
            scale(downImageRef, "up");
          }}
          onMouseLeave={() => {
            scale(downImageRef, "up");
          }}
        >
          <StepImage
            ref={ref => {
              downImageRef = ref;
            }}
            down
          />
        </StepButtonDown>
      </StepsButtons>
    </Wrapper>
  );
};

export default Stepper;
